import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';
const API_BASE_URL = config.API_BASE_URL;
const EXERCISE_DEFINITIONS_API_URL = `${API_BASE_URL}/api/v1/exercise-definitions`;
/**
 * Searches for exercise definitions using the advanced search endpoint.
 * Returns scored results with match type information.
 *
 * Response format:
 * {
 *   results: [
 *     {
 *       id: string,
 *       name: string,
 *       score: number,
 *       match_type: 'exact_name' | 'exact_alias' | 'name_starts' | 'alias_starts' | 'name_contains' | 'alias_contains' | 'partial',
 *       matched_on: string | null,
 *       primary_muscle_groups: string[] | null,
 *       is_bodyweight: boolean | null,
 *       _isPartial: true,
 *       _searchMeta: { score, match_type, matched_on }
 *     }
 *   ],
 *   total_count: number,
 *   query: string
 * }
 *
 * @param {object} payload - Search payload (query, filters, limit, offset).
 * @returns {Promise<{results: Array, total_count: number, query: string}>} Search response with scored results.
 */
export const searchExerciseDefinitionsAdvanced = async (payload) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/search`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to search exercises: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const data = await response.json();
    return {
      ...data,
      results: (data.results || []).map(hit => ({
        ...hit,
        _isPartial: true,
        _searchMeta: {
          score: hit.score,
          match_type: hit.match_type,
          matched_on: hit.matched_on,
        }
      }))
    };
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error performing advanced search for exercises:', error);
    authService.trackNetworkError('searchExerciseDefinitionsAdvanced', error);
    return { results: [], total_count: 0, query: payload.query };
  }
};
/**
 * Searches for exercise definitions using the full object search endpoint.
 * Use this when you need complete exercise data, not just search hits.
 *
 * @param {object} payload - Search payload (query, filters, limit, offset).
 * @param {string} [fields] - Optional comma-separated list of fields to return.
 * @returns {Promise<Array<Object>>} An array of full exercise definition objects.
 */
export const searchExerciseDefinitionsFull = async (payload, fields = null) => {
  let url = `${EXERCISE_DEFINITIONS_API_URL}/search/full`;
  if (fields) {
    url += `?fields=${encodeURIComponent(fields)}`;
  }
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to search exercises: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error performing full search for exercises:', error);
    authService.trackNetworkError('searchExerciseDefinitionsFull', error);
    return [];
  }
};
/**
 * Searches the catalog of exercise definitions using the legacy GET endpoint.
 *
 * @param {object} params - The query parameters.
 * @param {string} [params.query] - A search term.
 * @param {string} [params.muscle_group] - A muscle group to filter by.
 * @param {string} [params.scope] - The scope of resources to return (e.g., 'personalized').
 * @param {string} [params.fields] - Comma-separated list of fields to return.
 * @returns {Promise<Array<Object>>} An array of exercise definition objects.
 */
export const searchExerciseDefinitions = async (params = {}) => {
  const url = new URL(EXERCISE_DEFINITIONS_API_URL);
  if (params.query) url.searchParams.append('query', params.query);
  if (params.muscle_group) url.searchParams.append('muscle_group', params.muscle_group);
  if (params.scope) url.searchParams.append('scope', params.scope);
  if (params.fields) url.searchParams.append('fields', params.fields);
  console.log(`[workoutsmart/exerciseDefinitionApi] Searching exercise definitions from: ${url}`);
  try {
    const response = await authService.authFetch(url.toString());
    if (!response.ok) {
      let errorDetail = `Failed to search exercise definitions: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const definitions = await response.json();
    if (params.fields) {
      return definitions.map(def => ({ ...def, _isPartial: true }));
    }
    return definitions;
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error searching exercise definitions:', error);
    authService.trackNetworkError('searchExerciseDefinitions', error);
    throw error;
  }
};
/**
 * Fetches a single exercise definition by ID.
 * Use this to get full details when you only have a partial record from search.
 *
 * @param {string} id - The ID of the exercise definition.
 * @returns {Promise<Object>} The complete exercise definition object.
 */
export const getExerciseDefinitionById = async (id) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${id}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch exercise definition: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error fetching exercise definition by ID:', error);
    authService.trackNetworkError('getExerciseDefinitionById', error);
    throw error;
  }
};
/**
 * Creates a new exercise definition.
 *
 * @param {object} exerciseData - The exercise data to create.
 * @param {string} exerciseData.name - Required. The exercise name.
 * @param {string} [exerciseData.description] - Optional description.
 * @param {string[]} [exerciseData.aliases] - Optional aliases.
 * @param {string[]} [exerciseData.primary_muscle_groups] - Primary muscles worked.
 * @param {string[]} [exerciseData.secondary_muscle_groups] - Secondary muscles worked.
 * @param {object} [exerciseData.taxonomy] - Exercise taxonomy (modality, mechanics, etc).
 * @param {object} [exerciseData.setup] - Exercise setup details.
 * @param {object} [exerciseData.equipment] - Equipment requirements.
 * @param {boolean} [exerciseData.as_system] - Admin only: create as system record.
 * @returns {Promise<Object>} The created exercise definition.
 */
export const createExerciseDefinition = async (exerciseData) => {
  const url = EXERCISE_DEFINITIONS_API_URL;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: exerciseData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to create exercise definition: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error creating exercise definition:', error);
    authService.trackNetworkError('createExerciseDefinition', error);
    throw error;
  }
};
/**
 * Updates an existing exercise definition.
 *
 * @param {string} exerciseId - The ID of the exercise to update.
 * @param {object} exerciseData - The fields to update.
 * @returns {Promise<Object>} The updated exercise definition.
 */
export const updateExerciseDefinition = async (exerciseId, exerciseData) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${exerciseId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: exerciseData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update exercise definition: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error updating exercise definition:', error);
    authService.trackNetworkError('updateExerciseDefinition', error);
    throw error;
  }
};
/**
 * Deletes an exercise definition.
 *
 * @param {string} exerciseId - The ID of the exercise to delete.
 * @returns {Promise<void>}
 */
export const deleteExerciseDefinition = async (exerciseId) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${exerciseId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      let errorDetail = `Failed to delete exercise definition: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error deleting exercise definition:', error);
    authService.trackNetworkError('deleteExerciseDefinition', error);
    throw error;
  }
};
/**
 * Generates a complete exercise definition from partial data using AI.
 *
 * @param {object} partialExerciseData - Partial exercise data to complete.
 * @param {string} [partialExerciseData.name] - The exercise name.
 * @param {string} [partialExerciseData.description] - Optional description hint.
 * @param {string} [partialExerciseData.modalities] - Optional modalities hint.
 * @param {string[]} [partialExerciseData.primary_muscle_groups] - Optional muscle groups hint.
 * @returns {Promise<Object>} The AI-completed exercise definition (not yet saved).
 */
export const generateExerciseDefinition = async (partialExerciseData) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/complete`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: partialExerciseData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to generate exercise definition: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/exerciseDefinitionApi] Error generating exercise definition:', error);
    authService.trackNetworkError('generateExerciseDefinition', error);
    throw error;
  }
};
/**
 * Fetches the performance report for an exercise definition.
 *
 * @param {string} exerciseDefinitionId - The ID of the exercise.
 * @returns {Promise<Object>} The exercise performance report.
 */
export const getExerciseDefinitionReport = async (exerciseDefinitionId) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${exerciseDefinitionId}/report`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch exercise report: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[workoutsmart/exerciseDefinitionApi] Error fetching exercise report for ${exerciseDefinitionId}:`, error);
    authService.trackNetworkError('getExerciseDefinitionReport', error);
    throw error;
  }
};

/**
 * Triggers an AI-powered refresh of instructional videos for an exercise.
 * This is a long-running operation (2-5s) and should show a loading state in the UI.
 *
 * @param {string} exerciseId - The ID of the exercise to update.
 * @returns {Promise<Object>} The updated ExerciseDefinitionDetails object with new videos.
 */
export const refreshExerciseVideos = async (exerciseId) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${exerciseId}/videos/refresh`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
    });
    if (!response.ok) {
      let errorDetail = `Failed to refresh exercise videos: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[workoutsmart/exerciseDefinitionApi] Error refreshing videos for ${exerciseId}:`, error);
    authService.trackNetworkError('refreshExerciseVideos', error);
    throw error;
  }
};

/**
 * Fetches the extended details for an exercise definition.
 * This includes instructional content, videos, tips, etc.
 *
 * @param {string} exerciseId - The ID of the exercise.
 * @returns {Promise<Object|null>} The exercise details or null if not found.
 */
export const getExerciseDefinitionDetails = async (exerciseId) => {
  const url = `${EXERCISE_DEFINITIONS_API_URL}/${exerciseId}/details`;
  try {
    const response = await authService.authFetch(url);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      let errorDetail = `Failed to fetch exercise details: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[workoutsmart/exerciseDefinitionApi] Error fetching exercise details for ${exerciseId}:`, error);
    throw error;
  }
};